import { useQuery } from "@tanstack/react-query";
import { createBrowserRouter, Navigate, useParams } from "react-router-dom";
import { useEffect, type ReactNode } from "react";
import { AppShell } from "./components/layout/AppShell";
import { BillingStatusGate } from "./components/auth/BillingStatusGate";
import { ProtectedRoute } from "./components/auth/ProtectedRoute";
import { useAuth } from "./context/AuthContext";
import { TierGate } from "./components/TierGate";
import { LoginPage } from "./pages/Login";
import { ForgotPasswordPage } from "./pages/ForgotPassword";
import { ResetPasswordPage } from "./pages/ResetPassword";
import { Dashboard } from "./pages/dashboard/Dashboard";
import { VisitsListPage } from "./pages/visits/VisitsList";
import { VisitDetailsPage } from "./pages/visits/VisitDetails";
import { ResponseEntryPage } from "./pages/visits/ResponseEntry";
import { FindingsReviewPage } from "./pages/visits/FindingsReview";
import { ComplianceTrendsPage } from "./pages/ComplianceTrends";
import { TrainingAssignmentsPage } from "./pages/TrainingAssignments";
import { Anes008StagedLearnerPage } from "./pages/training/Anes008StagedLearnerPage";
import { Med005StagedLearnerPage } from "./pages/training/Med005StagedLearnerPage";
import { ReportsListPage } from "./pages/reports/ReportsList";
import { ReportDetailsPage } from "./pages/reports/ReportDetails";
import { ActionsListPage } from "./pages/actions/ActionsList";
import { ActionDetailsPage } from "./pages/actions/ActionDetails";
import { ReviewerQueuePage } from "./pages/reviewer/ReviewerQueue";
import { ReviewerDetailPage } from "./pages/reviewer/ReviewerDetail";
import { AdminUsersListPage } from "./pages/admin/AdminUsersList";
import { AdminUserDetailPage } from "./pages/admin/AdminUserDetail";
import { FacilityAccessPage } from "./pages/admin/FacilityAccess";
import { FacilitySetupPage } from "./pages/admin/FacilitySetup";
import { UserManagementPage } from "./pages/admin/UserManagement";
import { ReleasesListPage } from "./pages/superadmin/ReleasesList";
import { ReleaseDetailPage } from "./pages/superadmin/ReleaseDetail";
import { OrganizationAccessPage } from "./pages/superadmin/OrganizationAccess";
import { DemoRequestsListPage } from "./pages/superadmin/DemoRequestsList";
import { SmeFeedbackBacklogPage } from "./pages/superadmin/SmeFeedbackBacklog";
import { IntegrationsStatusPage } from "./pages/integrations/IntegrationsStatus";
import { IntegrationRunsPage } from "./pages/integrations/IntegrationRuns";
import { SelfMonitoringDashboardPage } from "./pages/selfMonitoring/SelfMonitoringDashboard";
import { SelfMonitoringChecklistPage } from "./pages/selfMonitoring/SelfMonitoringChecklist";
import { DailyAssessmentPage } from "./pages/selfMonitoring/DailyAssessment";
import { AcquisitionAssessmentPage } from "./pages/assessments/AcquisitionAssessment";
import { AcquisitionDDFormPage } from "./pages/assessments/AcquisitionDDForm";
import { AcquisitionReportPage } from "./pages/assessments/AcquisitionReport";
import { ServiceLineAssessmentPage } from "./pages/assessments/ServiceLineAssessment";
import {
  canManageBillingWorkspace,
  canManageFacilityAccess,
  canManageOrganizationAccess,
} from "./lib/services/adminUsers";
import { canManageStateRegulatory } from "./lib/services/stateRegulatory";
import { StateRegulatoryPage } from "./pages/admin/state-regulatory/StateRegulatoryPage";
import { StateComparePage } from "./pages/admin/state-regulatory/StateComparePage";
import { StateImportWizard } from "./pages/admin/state-regulatory/StateImportWizard";
import { canManageIntegrationWorkspace } from "./lib/services/integrations";
import { surveyRoutes } from "./pages/survey";
import { prestudyAuthRoutes } from "./pages/prestudy";
import { FacilityContactsPage } from "./pages/facilities/FacilityContacts";
import { FieldSurveyorSignupPage } from "./pages/billing/FieldSurveyorSignupPage";
import { FacilitiesListPage } from "./pages/fieldSurveyor/FacilitiesListPage";
import { FacilityNewPage } from "./pages/fieldSurveyor/FacilityNewPage";
import { FacilityDetailPage } from "./pages/fieldSurveyor/FacilityDetailPage";
import { ContactNewPage } from "./pages/fieldSurveyor/ContactNewPage";
import { FieldSurveyorProfilePage } from "./pages/fieldSurveyor/FieldSurveyorProfilePage";
import { SurveyorTodayPage } from "./pages/fieldSurveyor/SurveyorTodayPage";
import { VisitNewPage } from "./pages/fieldSurveyor/VisitNewPage";
import { MFASetupPage } from "./pages/profile/MFASetupPage";
import { ChartAuditDashboardPage } from "./pages/chart-audit/ChartAuditDashboard";
import { ChartAuditBatchPage } from "./pages/chart-audit/ChartAuditBatch";
import { ChartAuditPortfolioPage } from "./pages/chart-audit/ChartAuditPortfolio";
import { ChartAuditQapiPage } from "./pages/chart-audit/ChartAuditQapi";
import { ChartAuditTrendsPage } from "./pages/chart-audit/ChartAuditTrends";
import { QapiLeadershipWorkspacePage } from "./pages/qapi/QapiLeadershipWorkspace";
import { BillingWorkspacePage } from "./pages/billing/BillingWorkspace";
import { SignupPage } from "./pages/billing/SignupPage";
import { OnboardingPage } from "./pages/billing/OnboardingPage";
import { InsuranceDashboardPage } from "./pages/insurance/InsuranceDashboard";
import { getFacilityFeatureFlags } from "./lib/services/facilities";
import {
  canAccessPolicyWorkspace,
  canManageCredentialingWorkspace,
  canManageInsuranceWorkspace,
  canManagePatWorkspace,
  canManagePatientCommsWorkspace,
} from "./lib/services/adminUsers";
import { ConsultingServicesPage } from "./pages/marketing/ConsultingServicesPage";
import { PolicyLibraryPage } from "./pages/policies/PolicyLibraryPage";
import { CredentialingWorkspacePage } from "./pages/credentialing/CredentialingWorkspacePage";
import { PatientCommsBoardPage } from "./pages/patient-comms/PatientCommsBoardPage";
import { FamilyStatusPage } from "./pages/patient-comms/FamilyStatusPage";
import { PatWorkspacePage } from "./pages/pat/PatWorkspacePage";
import { PatIntakePage } from "./pages/pat/PatIntakePage";
import { canAccessQapiLeadershipWorkspace } from "./lib/services/qapiLeadership";
import { appHostExternalRouteUrl, isGuidedSurveyPwaHost } from "./lib/fieldAppHost";
import { canAccessReviewerWorkbench } from "./lib/services/reviewerWorkbench";

function AccessGate({
  allow,
  children,
}: {
  allow: (viewer: ReturnType<typeof useAuth>["viewerContext"]) => boolean;
  children: ReactNode;
}) {
  const { isLoading, viewerContext } = useAuth();

  if (isLoading) {
    return (
      <div className="p-8 text-sm text-slate-500">
        Loading access profile...
      </div>
    );
  }

  if (!allow(viewerContext)) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}

function canAccessChartAuditPortfolio(
  viewer: ReturnType<typeof useAuth>["viewerContext"],
) {
  if (!viewer) return false;
  // #1897: empty/unknown enterprise scope is not portfolio access (fail closed)
  if (viewer.tier === "consultant") return true;
  if (viewer.tier !== "enterprise") return false;
  if (
    viewer.facility_scope_status === "unknown" ||
    viewer.facility_scope_status === "none"
  ) {
    return false;
  }
  return viewer.facility_ids.length !== 1;
}

function FeatureFlagGate({
  featureKey,
  children,
}: {
  featureKey: string;
  children: ReactNode;
}) {
  const featureFlagsQuery = useQuery({
    queryKey: ["route-feature-flags", featureKey],
    queryFn: () => getFacilityFeatureFlags(),
    staleTime: 5 * 60 * 1000,
  });

  if (featureFlagsQuery.isLoading) {
    return (
      <div className="p-8 text-sm text-slate-500">
        Loading workspace access...
      </div>
    );
  }

  const enabled = (featureFlagsQuery.data ?? []).some(
    (flag) => flag.feature_key === featureKey && flag.enabled,
  );
  if (!enabled) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}

export function FieldAppRouteGate({ children }: { children: ReactNode }) {
  const { viewerContext } = useAuth();
  const pathname = globalThis.location?.pathname ?? "/";
  const search = globalThis.location?.search ?? "";
  const hash = globalThis.location?.hash ?? "";
  const fieldAppRedirect = appHostExternalRouteUrl(pathname, search, hash, viewerContext?.role ?? null);

  if (isGuidedSurveyPwaHost() && fieldAppRedirect) {
    if (fieldAppRedirect.startsWith("/")) {
      return <Navigate to={fieldAppRedirect} replace />;
    }

    return <ExternalRouteRedirect to={fieldAppRedirect} />;
  }

  return <>{children}</>;
}

function SurveyVisitRedirect() {
  const { visitId } = useParams<{ visitId: string }>();
  return <Navigate to={`/visits/${visitId}`} replace />;
}

function ExternalRouteRedirect({ to }: { to: string }) {
  useEffect(() => {
    globalThis.location?.replace(to);
  }, [to]);

  return (
    <div className="p-8 text-sm text-slate-500">
      Opening normal app...
    </div>
  );
}

function WildcardRedirect() {
  const { viewerContext } = useAuth();
  if (viewerContext?.role === "FieldSurveyor") {
    return <Navigate to="/today" replace />;
  }
  return <Navigate to={isGuidedSurveyPwaHost() ? "/visits" : "/dashboard"} replace />;
}

export const router = createBrowserRouter([
  {
    path: "/login",
    element: <LoginPage />,
  },
  {
    path: "/signup",
    element: <SignupPage />,
  },
  {
    path: "/signup/field-surveyor",
    element: <FieldSurveyorSignupPage />,
  },
  {
    path: "/services",
    element: <ConsultingServicesPage />,
  },
  {
    path: "/family-status/:token",
    element: <FamilyStatusPage />,
  },
  {
    path: "/pat-intake/:token",
    element: <PatIntakePage />,
  },
  {
    path: "/forgot-password",
    element: <ForgotPasswordPage />,
  },
  {
    path: "/reset-password",
    element: <ResetPasswordPage />,
  },
  {
    path: "/",
    element: (
      <ProtectedRoute>
        <FieldAppRouteGate>
          <BillingStatusGate>
            <AppShell />
          </BillingStatusGate>
        </FieldAppRouteGate>
      </ProtectedRoute>
    ),
    children: [
      {
        index: true,
        element: <Navigate to="/dashboard" replace />,
      },
      {
        path: "profile/mfa",
        element: <MFASetupPage />,
      },
      {
        path: "dashboard",
        element: <Dashboard />,
      },
      {
        path: "visits",
        children: [
          {
            path: "new",
            element: <VisitNewPage />,
          },
          {
            index: true,
            element: <VisitsListPage />,
          },
          {
            path: ":visitId",
            children: [
              {
                index: true,
                element: <VisitDetailsPage />,
              },
              {
                path: "review",
                element: <FindingsReviewPage />,
              },
              {
                path: "standard/:standardId",
                element: <ResponseEntryPage />,
              },
              ...surveyRoutes,
              ...prestudyAuthRoutes,
            ],
          },
        ],
      },
      {
        path: "surveys",
        children: [
          {
            index: true,
            element: <Navigate to="/visits" replace />,
          },
          {
            path: ":visitId",
            element: <SurveyVisitRedirect />,
          },
        ],
      },
      {
        path: "today",
        element: <SurveyorTodayPage />,
      },
      {
        path: "facilities",
        children: [
          {
            index: true,
            element: <FacilitiesListPage />,
          },
          {
            path: "new",
            element: <FacilityNewPage />,
          },
          {
            path: ":facilityId",
            element: <FacilityDetailPage />,
          },
          {
            path: ":facilityId/contacts",
            element: <FacilityContactsPage />,
          },
          {
            path: ":facilityId/contacts/new",
            element: <ContactNewPage />,
          },
        ],
      },
      {
        path: "profile",
        element: <FieldSurveyorProfilePage />,
      },
      {
        path: "trends",
        element: <ComplianceTrendsPage />,
      },
      {
        path: "training/media/anes-008",
        element: <Anes008StagedLearnerPage />,
      },
      {
        path: "training/media/med-005",
        element: <Med005StagedLearnerPage />,
      },
      {
        path: "training/*",
        element: <TrainingAssignmentsPage />,
      },
      {
        path: "reports",
        children: [
          {
            index: true,
            element: <ReportsListPage />,
          },
          {
            path: ":reportId",
            element: <ReportDetailsPage />,
          },
        ],
      },
      {
        path: "billing",
        element: (
          <AccessGate allow={canManageBillingWorkspace}>
            <BillingWorkspacePage />
          </AccessGate>
        ),
      },
      {
        path: "insurance",
        element: (
          <AccessGate allow={canManageInsuranceWorkspace}>
            <InsuranceDashboardPage />
          </AccessGate>
        ),
      },
      {
        path: "policies",
        element: (
          <FeatureFlagGate featureKey="policy_management">
            <AccessGate allow={canAccessPolicyWorkspace}>
              <PolicyLibraryPage />
            </AccessGate>
          </FeatureFlagGate>
        ),
      },
      {
        path: "credentialing",
        element: (
          <FeatureFlagGate featureKey="credentialing">
            <AccessGate allow={canManageCredentialingWorkspace}>
              <CredentialingWorkspacePage />
            </AccessGate>
          </FeatureFlagGate>
        ),
      },
      {
        path: "pat",
        element: (
          <FeatureFlagGate featureKey="pat_automation">
            <AccessGate allow={canManagePatWorkspace}>
              <PatWorkspacePage />
            </AccessGate>
          </FeatureFlagGate>
        ),
      },
      {
        path: "patient-comms",
        element: (
          <FeatureFlagGate featureKey="patient_comms">
            <AccessGate allow={canManagePatientCommsWorkspace}>
              <PatientCommsBoardPage />
            </AccessGate>
          </FeatureFlagGate>
        ),
      },
      {
        path: "onboarding",
        element: (
          <AccessGate allow={canManageBillingWorkspace}>
            <OnboardingPage />
          </AccessGate>
        ),
      },
      {
        path: "actions",
        children: [
          {
            index: true,
            element: <ActionsListPage />,
          },
          {
            path: ":actionId",
            element: <ActionDetailsPage />,
          },
        ],
      },
      {
        path: "reviewer",
        element: (
          <AccessGate allow={(viewer) => (viewer ? canAccessReviewerWorkbench(viewer.role) : false)}>
            <ReviewerQueuePage />
          </AccessGate>
        ),
      },
      {
        path: "reviewer/:artifactType/:artifactId",
        element: (
          <AccessGate allow={(viewer) => (viewer ? canAccessReviewerWorkbench(viewer.role) : false)}>
            <ReviewerDetailPage />
          </AccessGate>
        ),
      },
      {
        path: "admin",
        children: [
          {
            path: "facility-access",
            element: (
              <AccessGate allow={canManageFacilityAccess}>
                <FacilityAccessPage />
              </AccessGate>
            ),
          },
          {
            path: "users",
            children: [
              {
                index: true,
                element: (
                  <AccessGate allow={(vc) => vc?.is_superadmin === true}>
                    <AdminUsersListPage />
                  </AccessGate>
                ),
              },
              {
                path: ":userId",
                element: (
                  <AccessGate allow={(vc) => vc?.is_superadmin === true}>
                    <AdminUserDetailPage />
                  </AccessGate>
                ),
              },
            ],
          },
          {
            path: "facility-setup",
            element: (
              <AccessGate allow={(vc) => vc?.is_superadmin === true}>
                <FacilitySetupPage />
              </AccessGate>
            ),
          },
          {
            path: "user-management",
            element: (
              <AccessGate allow={(vc) => vc?.is_superadmin === true}>
                <UserManagementPage />
              </AccessGate>
            ),
          },
        ],
      },
      {
        path: "superadmin",
        children: [
          {
            path: "organization-access",
            element: (
              <AccessGate allow={canManageOrganizationAccess}>
                <OrganizationAccessPage />
              </AccessGate>
            ),
          },
          {
            path: "demo-requests",
            element: (
              <AccessGate allow={(vc) => vc?.is_superadmin === true}>
                <DemoRequestsListPage />
              </AccessGate>
            ),
          },
          {
            path: "sme-feedback",
            element: (
              <AccessGate allow={(vc) => vc?.is_superadmin === true}>
                <SmeFeedbackBacklogPage />
              </AccessGate>
            ),
          },
          {
            path: "releases",
            children: [
              {
                index: true,
                element: (
                  <AccessGate allow={(vc) => vc?.is_superadmin === true}>
                    <ReleasesListPage />
                  </AccessGate>
                ),
              },
              {
                path: ":releaseId",
                element: (
                  <AccessGate allow={(vc) => vc?.is_superadmin === true}>
                    <ReleaseDetailPage />
                  </AccessGate>
                ),
              },
            ],
          },
          {
            path: "state-regulatory",
            children: [
              {
                index: true,
                element: (
                  <AccessGate allow={canManageStateRegulatory}>
                    <StateRegulatoryPage />
                  </AccessGate>
                ),
              },
              {
                path: "compare",
                element: (
                  <AccessGate allow={canManageStateRegulatory}>
                    <StateComparePage />
                  </AccessGate>
                ),
              },
              {
                path: "import",
                element: (
                  <AccessGate allow={canManageStateRegulatory}>
                    <StateImportWizard />
                  </AccessGate>
                ),
              },
            ],
          },
        ],
      },
      {
        path: "integrations",
        children: [
          {
            path: "status",
            element: (
              <AccessGate allow={canManageIntegrationWorkspace}>
                <IntegrationsStatusPage />
              </AccessGate>
            ),
          },
          {
            path: "runs",
            element: (
              <AccessGate allow={canManageIntegrationWorkspace}>
                <IntegrationRunsPage />
              </AccessGate>
            ),
          },
        ],
      },
      {
        path: "self-monitoring",
        children: [
          {
            index: true,
            element: <SelfMonitoringDashboardPage />,
          },
          {
            path: "checklist",
            element: <SelfMonitoringChecklistPage />,
          },
          {
            path: "daily",
            element: <DailyAssessmentPage />,
          },
        ],
      },
      {
        path: "qapi",
        element: (
          <FeatureFlagGate featureKey="qapi">
            <AccessGate allow={canAccessQapiLeadershipWorkspace}>
              <QapiLeadershipWorkspacePage />
            </AccessGate>
          </FeatureFlagGate>
        ),
      },
      {
        path: "chart-audit",
        children: [
          {
            index: true,
            element: <ChartAuditDashboardPage />,
          },
          {
            path: ":batchId",
            element: <ChartAuditBatchPage />,
          },
          {
            path: "qapi",
            element: <ChartAuditQapiPage />,
          },
          {
            path: "trends",
            element: <ChartAuditTrendsPage />,
          },
          {
            path: "portfolio",
            element: (
              <AccessGate allow={canAccessChartAuditPortfolio}>
                <ChartAuditPortfolioPage />
              </AccessGate>
            ),
          },
        ],
      },
      {
        path: "assessments",
        children: [
          {
            path: "acquisition",
            element: (
              <TierGate tiers={["enterprise"]}>
                <AcquisitionAssessmentPage />
              </TierGate>
            ),
          },
          {
            path: "acquisition/:visitId",
            element: (
              <TierGate tiers={["enterprise"]}>
                <AcquisitionDDFormPage />
              </TierGate>
            ),
          },
          {
            path: "acquisition/:visitId/report",
            element: (
              <TierGate tiers={["enterprise"]}>
                <AcquisitionReportPage />
              </TierGate>
            ),
          },
          {
            path: "service-line",
            element: <ServiceLineAssessmentPage />,
          },
        ],
      },
    ],
  },
  {
    path: "*",
    element: <WildcardRedirect />,
  },
]);
